import {SetStateAction, useCallback, useEffect, useMemo, useState} from 'react';
import {Bar} from 'react-chartjs-2';
import {IoPersonSharp} from "react-icons/io5";
import {format, subDays} from 'date-fns';
import {client} from '../dataProvider';
import {
    Box,
    Chip,
    CircularProgress,
    Grid,
    IconButton,
    Paper,
    TextField,
    TextFieldProps,
    Typography,
    useTheme
} from '@material-ui/core';
import {Alert, Autocomplete} from '@material-ui/lab';
import {DatePicker} from '@material-ui/pickers';
import {IoMdRefresh} from "react-icons/io";
import {FaDownload, FaSearch} from 'react-icons/fa';

type ChartDataPerUser = Record<string, Record<string, { validated: number; unvalidated: number }>>;
type UserOption = { id: number; label: string };

export const CheckCounter = () => {
  const theme = useTheme();
  const [data, setData] = useState<ChartDataPerUser>({});
  const [options, setOptions] = useState<UserOption[]>([]);
  const [labels, setLabels] = useState<string[]>([]);
  const [selectedDateFrom, setSelectedDateFrom] = useState<Date | null>(subDays(new Date(), 7));
  const [selectedDateTo, setSelectedDateTo] = useState<Date | null>(new Date());
  const [selectedUsers, setSelectedUsers] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const formatDate = useCallback((date: Date | null): string => date ? format(date, 'yyyy-MM-dd') : '', []);
  const handleDateChangeFrom = useCallback((date: Date | null) => setSelectedDateFrom(date), []);
  const handleDateChangeTo = useCallback((date: Date | null) => setSelectedDateTo(date), []);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          font: {
            family: theme.typography.fontFamily,
            size: 12
          }
        }
      },
      tooltip: {
        backgroundColor: theme.palette.primary.main,
        titleFont: {
          family: theme.typography.fontFamily,
          size: 14
        },
        bodyFont: {
          family: theme.typography.fontFamily,
          size: 13
        },
        padding: 10,
        cornerRadius: 4
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            family: theme.typography.fontFamily
          }
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: theme.palette.divider
        },
        ticks: {
          font: {
            family: theme.typography.fontFamily
          }
        }
      }
    },
    animation: {
      duration: 750,
      easing: 'easeOutQuart' as const
    }
  }), [theme]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await client('autocomplete/user', {
          method: 'GET', params: {
            page: 1,
            page_size: 1000
          }
        });
        setOptions(response.results || []);
      } catch (err) {
        console.error("Errore durante il recupero degli utenti:", err);
        setError("Impossibile caricare l'elenco degli utenti");
      }
    };
    fetchUsers();
  }, []);

  const fetchStatistics = useCallback(async () => {
    if (!selectedDateFrom || !selectedDateTo) {
      setError("Seleziona un intervallo di date valido");
      return;
    }

    setLoading(true);
    setError(null);

    const start = formatDate(selectedDateFrom);
    const end = formatDate(selectedDateTo);
    const userIds = selectedUsers.map(user => String(user.id)).join(",");

    try {
      const response = await client(
        `validation_stats/?date_start=${start}&date_end=${end}&users=${userIds}`,
        { method: "GET" }
      );
      setData(response || {});
      const uniqueDates = new Set<string>();

      for (const userId in response) {
        for (const date in response[userId]) {
          uniqueDates.add(date);
        }
      }
      const sortedDates = Array.from(uniqueDates).sort();
      setLabels(sortedDates);

      if (Object.keys(response || {}).length === 0) {
        setError("Nessun dato disponibile per i criteri selezionati");
      }
    } catch (err) {
      console.error("Errore durante il recupero delle statistiche:", err);
      setError("Si è verificato un errore durante il caricamento dei dati");
    } finally {
      setLoading(false);
    }
  }, [selectedDateFrom, selectedDateTo, selectedUsers, formatDate]);

  const exportCSV = useCallback(() => {
    if (!data || Object.keys(data).length === 0) return;
    let csvContent = "Dipendente,Data,Validati,Invalidati\n";
    for (const userId in data) {
      for (const date in data[userId]) {
        const { validated, unvalidated } = data[userId][date];
        csvContent += `${userId},${date},${validated},${unvalidated}\n`;
      }
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.setAttribute('href', url);
    link.setAttribute('download', `validation-stats-${formatDate(selectedDateFrom)}-to-${formatDate(selectedDateTo)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [data, selectedDateFrom, selectedDateTo, formatDate]);

  return (
    <Box>
      <Paper style={{ padding: 24 }}>
        <Typography variant="h4" component="h1" gutterBottom style={{ marginBottom: 24, fontWeight: 'bold' }}>
          Statistiche Validazione
        </Typography>

        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={3}>
            <DatePicker
              label="Data da"
              value={selectedDateFrom}
              onChange={handleDateChangeFrom}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <DatePicker
              label="Data a"
              value={selectedDateTo}
              onChange={handleDateChangeTo}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Autocomplete
              multiple
              options={options}
              getOptionLabel={(option: UserOption) => option.label}
              onChange={(_: any, value: SetStateAction<UserOption[]>) => setSelectedUsers(value)}
              value={selectedUsers}
              renderInput={(params: JSX.IntrinsicAttributes & TextFieldProps) => (
                <TextField {...params} label="Utenti" variant="outlined" fullWidth />
              )}
              renderTags={(value: any[], getTagProps: (arg0: { index: any; }) => any) =>
                value.map((option, index) => {
                  return (
                    <Chip
                      avatar={<IoPersonSharp />}
                      label={option.label}
                      {...getTagProps({ index })}
                      sx={{ backgroundColor: theme.palette.primary.light, color: 'white' }} />
                  );
                })
              }
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <IconButton
              onClick={fetchStatistics}
              title="Ricerca"
            >
              <FaSearch />
            </IconButton>
            <IconButton
              onClick={exportCSV}
              disabled={!data || Object.keys(data).length === 0}
              title="Esporta CSV"
            >
              <FaDownload />
            </IconButton>
            <IconButton
              onClick={fetchStatistics}
              color="primary"
              title="Aggiorna dati"
            >
              <IoMdRefresh />
            </IconButton>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="warning">{error}</Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && !error && Object.keys(data).length === 0 && (
        <Paper elevation={2}>
          <Typography variant="h6" color="textSecondary">
            Nessun dato disponibile. Modifica i criteri di ricerca o seleziona gli utenti.
          </Typography>
        </Paper>
      )}

      <Grid container>
        {!loading && Object.entries(data).map(([userId, userData]) => (
          <Grid item xs={12} md={6} key={userId}>
            <Paper>

              <Grid container alignItems="center" justifyContent="space-between">
                <Grid item xs={4} style={{ display: 'flex', alignItems: 'center', padding: 8 }}>
                  <Typography variant="h6" component="h4">
                    <IoPersonSharp /> {userId}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="h6" component="h4">
                    {labels.reduce((sum, date) => sum + (userData[date]?.validated || 0), 0)} validati
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="h6" component="h4">
                    {labels.reduce((sum, date) => sum + (userData[date]?.unvalidated || 0), 0)} invalidati
                  </Typography>
                </Grid>
              </Grid>
              <Box sx={{ height: 350 }}>
                <Bar
                  options={chartOptions}
                  data={{
                    labels,
                    datasets: [
                      {
                        label: "Validati",
                        backgroundColor: 'rgba(46, 125, 50, 0.7)',
                        borderColor: 'rgb(46, 125, 50)',
                        borderWidth: 1,
                        borderRadius: 4,
                        data: labels.map(date => userData[date]?.validated || 0),
                      },
                      {
                        label: "Invalidati",
                        backgroundColor: 'rgba(211, 47, 47, 0.7)',
                        borderColor: 'rgb(211, 47, 47)',
                        borderWidth: 1,
                        borderRadius: 4,
                        data: labels.map(date => userData[date]?.unvalidated || 0),
                      }
                    ]
                  }}
                />
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};